import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Heading,
  Modal,
  Paragraph,
  Tag,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';
import { IconCameraExposureFilled } from '@zillow/constellation-icons';
import { useRentalManagerUserPropertySpotlightWebApiHandler_apply } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

type Props = {
  isOpen: boolean;
  listingAlias: string;
  listingName: string;
  availabilityText: string;
  onClose: () => void;
  onSuccess: () => void;
};

const SpotlightApplyModal = ({ isOpen, listingAlias, listingName, availabilityText, onClose, onSuccess }: Props) => {
  const { enqueueToast } = useToast();
  const toastWithZillowTheme = (toast: React.ReactNode) => <div data-panda-theme="zillow">{toast}</div>;
  const { mutateAsync: applySpotlight, isPending } = useRentalManagerUserPropertySpotlightWebApiHandler_apply({
    onSuccess: () => {
      enqueueToast(
        toastWithZillowTheme(<Toast tone="success">Listing Spotlight successfully applied to {listingName}.</Toast>),
      );
      onSuccess();
    },
    onError: () => {
      enqueueToast(
        toastWithZillowTheme(<Toast tone="critical">Listing Spotlight couldn’t be applied. Try again.</Toast>),
      );
      return;
    },
  });

  const handleApply = async () => {
    await applySpotlight({ getParams: { listingAlias } });
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={(open) => !open && onClose()} size={{ base: 'fullScreen', md: 'md' }}>
      <Modal.Portal>
        <Modal.Backdrop>
          <Modal.Content data-panda-theme="zillow">
            <Modal.Header>
              <Heading level={2} textStyle="heading-sm">
                Apply Listing Spotlight
              </Heading>
            </Modal.Header>
            <Divider />
            <Modal.Body>
              <Box css={{ display: 'flex', flexDirection: 'column', gap: '400', alignItems: 'flex-start' }}>
                <Tag size="md" tone="blue" icon={<IconCameraExposureFilled />}>
                  {availabilityText}
                </Tag>
                <Paragraph>
                  Apply Listing Spotlight to {listingName} to increase visibility for this property.
                </Paragraph>
                <Alert tone="info" css={{ alignSelf: 'stretch' }}>
                  Allow up to 24 hours for Listing Spotlight updates to apply across all platforms
                </Alert>
              </Box>
            </Modal.Body>
            <Divider />
            <Modal.Footer>
              <ButtonGroup aria-label="Actions">
                <TextButton tone="brand" onClick={onClose}>
                  Close
                </TextButton>
                <Button
                  tone="brand"
                  emphasis="filled"
                  size="md"
                  loading={isPending}
                  disabled={isPending}
                  onClick={handleApply}
                >
                  Apply
                </Button>
              </ButtonGroup>
            </Modal.Footer>
            <Modal.CloseButton />
          </Modal.Content>
        </Modal.Backdrop>
      </Modal.Portal>
    </Modal.Root>
  );
};

export default SpotlightApplyModal;
